'use client';

import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { getApiBase } from '@/lib/apiBase';

const COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#EC4899', '#06B6D4', '#F97316',
  '#14B8A6', '#A855F7', '#84CC16', '#64748B',
];

interface AllocationItem {
  [key: string]: unknown;
  name: string;
  value: number;
}

// EODHD returns: { "SectorName": { "Equity_%": "25.5", "Relative_to_Category": "1.2" }, ... }
type EodhdWeightings = Record<string, { 'Equity_%': string | number; Relative_to_Category: string | number }>;

function parseWeightings(data: EodhdWeightings): AllocationItem[] {
  if (!data || typeof data !== 'object') return [];

  const parsed = Object.entries(data)
    .map(([name, vals]) => ({
      name,
      value: parseFloat(String(vals?.['Equity_%'] ?? 0)),
    }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);

  // Group items < 1% into "Other"
  const main: AllocationItem[] = [];
  let otherTotal = 0;

  for (const item of parsed) {
    if (item.value < 1) {
      otherTotal += item.value;
    } else {
      main.push(item);
    }
  }

  if (otherTotal > 0) {
    main.push({ name: 'Other', value: parseFloat(otherTotal.toFixed(2)) });
  }

  return main;
}

// EODHD Asset_Allocation: { "Cash": { "Net_Assets_%": "2.5", ... }, "Stock_US": { ... }, ... }
type EodhdAssetAllocation = Record<string, { 'Net_Assets_%'?: string | number }>;

const ASSET_NAME_MAP: Record<string, string> = {
  Stock_US: 'US Stocks',
  Stock_non_US: 'Non-US Stocks',
  NotClassified: 'Other',
};

function parseAssetAllocation(data: EodhdAssetAllocation): AllocationItem[] {
  if (!data || typeof data !== 'object') return [];

  return Object.entries(data)
    .map(([name, vals]) => ({
      name: ASSET_NAME_MAP[name] ?? name,
      value: parseFloat(String(vals?.['Net_Assets_%'] ?? 0)),
    }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);
}

// EODHD Top_10_Holdings: { "AAPL": { "Name": "Apple Inc", "Assets_%": 7.12, ... }, ... }
type EodhdTopHoldings = Record<string, { Name?: string; 'Assets_%'?: string | number }>;

function parseTopHoldings(data: EodhdTopHoldings): AllocationItem[] {
  if (!data || typeof data !== 'object') return [];

  return Object.entries(data)
    .map(([code, vals]) => ({
      name: vals?.Name ?? code,
      value: parseFloat(String(vals?.['Assets_%'] ?? 0)),
    }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);
}

// EODHD Market_Capitalisation: { "Mega": "45.2", "Big": "30.1", ... }
type EodhdMarketCap = Record<string, string | number>;

const MARKET_CAP_NAME_MAP: Record<string, string> = {
  Mega: 'Mega Cap',
  Big: 'Large Cap',
  Medium: 'Mid Cap',
  Small: 'Small Cap',
  Micro: 'Micro Cap',
};

function parseMarketCap(data: EodhdMarketCap): AllocationItem[] {
  if (!data || typeof data !== 'object') return [];

  return Object.entries(data)
    .map(([name, val]) => ({
      name: MARKET_CAP_NAME_MAP[name] ?? name,
      value: parseFloat(String(val ?? 0)),
    }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);
}

const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ name: string; value: number; payload: { fill: string } }> }) => {
  if (!active || !payload || payload.length === 0) return null;
  const item = payload[0];
  return (
    <div className="rounded-xl px-3 py-2 text-sm shadow-lg" style={{ backgroundColor: 'var(--color-chart-tooltip-bg)', border: `1px solid var(--color-chart-tooltip-border)`, color: 'var(--color-text)' }}>
      <span className="inline-block w-2.5 h-2.5 rounded-full mr-2" style={{ backgroundColor: item.payload.fill }} />
      {item.name}: {item.value.toFixed(2)}%
    </div>
  );
};

function AllocationSection({
  title,
  data,
  isLoading,
  error,
}: {
  title: string;
  data: AllocationItem[];
  isLoading: boolean;
  error: string | null;
}) {
  if (isLoading) {
    return (
      <div className="rounded-2xl p-6" style={{ backgroundColor: 'var(--color-surface)' }}>
        <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">{title}</h3>
        <div className="flex h-48 items-center justify-center">
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-[var(--color-border)] border-t-[var(--color-accent-subtle)]" />
            <p className="text-[var(--color-text-secondary)] text-sm">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl p-6" style={{ backgroundColor: 'var(--color-surface)' }}>
        <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">{title}</h3>
        <p className="text-[var(--color-text-muted)] text-sm text-center py-8">{error}</p>
      </div>
    );
  }

  if (data.length === 0) {
    return null;
  }

  return (
    <div className="rounded-2xl p-6" style={{ backgroundColor: 'var(--color-surface)' }}>
      <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">{title}</h3>
      <div className="flex flex-col md:flex-row items-center gap-6">
        {/* Donut Chart */}
        <div className="w-[220px] h-[220px] flex-shrink-0">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                strokeWidth={2}
                stroke="var(--color-surface-hover)"
              >
                {data.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Legend Table */}
        <div className="flex-1 w-full">
          <table className="w-full text-sm">
            <tbody>
              {data.map((item, index) => (
                <tr key={`${item.name}-${index}`} className="border-b border-[var(--color-border-subtle)] last:border-0">
                  <td className="py-1.5 pr-3">
                    <span
                      className="inline-block w-3 h-3 rounded-sm mr-2 align-middle"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-[var(--color-text-primary)]">{item.name}</span>
                  </td>
                  <td className="py-1.5 text-right text-[var(--color-text-secondary)] font-medium tabular-nums">
                    {item.value.toFixed(2)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

interface EtfAllocationsProps {
  symbol: string;
}

export const EtfAllocations: React.FC<EtfAllocationsProps> = ({ symbol }) => {
  const [sectorData, setSectorData] = useState<AllocationItem[]>([]);
  const [sectorLoading, setSectorLoading] = useState(true);
  const [sectorError, setSectorError] = useState<string | null>(null);

  const [countryData, setCountryData] = useState<AllocationItem[]>([]);
  const [countryLoading, setCountryLoading] = useState(true);
  const [countryError, setCountryError] = useState<string | null>(null);

  const [assetData, setAssetData] = useState<AllocationItem[]>([]);
  const [assetLoading, setAssetLoading] = useState(true);
  const [assetError, setAssetError] = useState<string | null>(null);

  const [marketCapData, setMarketCapData] = useState<AllocationItem[]>([]);
  const [marketCapLoading, setMarketCapLoading] = useState(true);
  const [marketCapError, setMarketCapError] = useState<string | null>(null);

  const [holdingsData, setHoldingsData] = useState<AllocationItem[]>([]);
  const [holdingsLoading, setHoldingsLoading] = useState(true);
  const [holdingsError, setHoldingsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchSectors() {
      setSectorLoading(true);
      setSectorError(null);
      try {
        const res = await fetch(`${getApiBase()}/api/etf/sector-weightings?symbol=${encodeURIComponent(symbol)}`);
        if (!res.ok) throw new Error('Failed to load sector data');
        const data = await res.json();
        if (!cancelled) {
          setSectorData(parseWeightings(data));
        }
      } catch {
        if (!cancelled) setSectorError('Unable to load sector allocations');
      } finally {
        if (!cancelled) setSectorLoading(false);
      }
    }

    fetchSectors();
    return () => { cancelled = true; };
  }, [symbol]);

  useEffect(() => {
    let cancelled = false;

    async function fetchCountries() {
      setCountryLoading(true);
      setCountryError(null);
      try {
        const res = await fetch(`${getApiBase()}/api/etf/country-weightings?symbol=${encodeURIComponent(symbol)}`);
        if (!res.ok) throw new Error('Failed to load country data');
        const data = await res.json();
        if (!cancelled) {
          setCountryData(parseWeightings(data));
        }
      } catch {
        if (!cancelled) setCountryError('Unable to load country allocations');
      } finally {
        if (!cancelled) setCountryLoading(false);
      }
    }

    fetchCountries();
    return () => { cancelled = true; };
  }, [symbol]);

  useEffect(() => {
    let cancelled = false;

    async function fetchAssetAllocation() {
      setAssetLoading(true);
      setAssetError(null);
      try {
        const res = await fetch(`${getApiBase()}/api/etf/asset-allocation?symbol=${encodeURIComponent(symbol)}`);
        if (!res.ok) throw new Error('Failed to load asset allocation data');
        const data = await res.json();
        if (!cancelled) {
          setAssetData(parseAssetAllocation(data));
        }
      } catch {
        if (!cancelled) setAssetError('Unable to load asset allocations');
      } finally {
        if (!cancelled) setAssetLoading(false);
      }
    }

    fetchAssetAllocation();
    return () => { cancelled = true; };
  }, [symbol]);

  useEffect(() => {
    let cancelled = false;

    async function fetchMarketCap() {
      setMarketCapLoading(true);
      setMarketCapError(null);
      try {
        const res = await fetch(`${getApiBase()}/api/etf/market-cap?symbol=${encodeURIComponent(symbol)}`);
        if (!res.ok) throw new Error('Failed to load market cap data');
        const data = await res.json();
        if (!cancelled) {
          setMarketCapData(parseMarketCap(data));
        }
      } catch {
        if (!cancelled) setMarketCapError('Unable to load market capitalization');
      } finally {
        if (!cancelled) setMarketCapLoading(false);
      }
    }

    fetchMarketCap();
    return () => { cancelled = true; };
  }, [symbol]);

  useEffect(() => {
    let cancelled = false;

    async function fetchHoldings() {
      setHoldingsLoading(true);
      setHoldingsError(null);
      try {
        const res = await fetch(`${getApiBase()}/api/etf/top-holdings?symbol=${encodeURIComponent(symbol)}`);
        if (!res.ok) throw new Error('Failed to load top holdings data');
        const data = await res.json();
        if (!cancelled) {
          setHoldingsData(parseTopHoldings(data));
        }
      } catch {
        if (!cancelled) setHoldingsError('Unable to load top holdings');
      } finally {
        if (!cancelled) setHoldingsLoading(false);
      }
    }

    fetchHoldings();
    return () => { cancelled = true; };
  }, [symbol]);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <AllocationSection
        title="Sector Allocation"
        data={sectorData}
        isLoading={sectorLoading}
        error={sectorError}
      />
      <AllocationSection
        title="Country / Region Allocation"
        data={countryData}
        isLoading={countryLoading}
        error={countryError}
      />
      <AllocationSection
        title="Asset Allocation"
        data={assetData}
        isLoading={assetLoading}
        error={assetError}
      />
      <AllocationSection
        title="Market Capitalization"
        data={marketCapData}
        isLoading={marketCapLoading}
        error={marketCapError}
      />
      <AllocationSection
        title="Top 10 Holdings"
        data={holdingsData}
        isLoading={holdingsLoading}
        error={holdingsError}
      />
    </div>
  );
};
